import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from 'context/AppContext';
import { persistence } from 'services/persistence';
import { getPathForPage } from 'utils/navigation';
import { generateUniqueId } from 'utils/id';
import type {
  ChatThread,
  ChatMessage,
  ChatContextSelection,
  ForYouItem,
  ForYouItemType,
} from 'types';
import type { FeatureRecommendation, OnboardingAnswer } from 'utils/onboardingRecommendations';
import { getTopRecommendations, generateOnboardingMessage } from 'utils/onboardingRecommendations';
import { STORAGE_KEYS } from 'constants/storageKeys';

/**
 * Handles onboarding completion: processes pending data from localStorage,
 * adds ForYou items, creates chat thread, and navigates to eir-chat.
 */
export function useOnboardingFlow(): void {
  const { dispatch } = useApp();
  const navigate = useNavigate();
  const onboardingProcessedRef = useRef(false);

  const createOnboardingChatThread = useCallback(
    (answers: OnboardingAnswer, recommendations: FeatureRecommendation[]) => {
      try {
        const messageContent = generateOnboardingMessage(recommendations, answers);
        if (!messageContent) {
          console.error('Failed to generate onboarding message');
          return;
        }

        const assistantMessage: ChatMessage = {
          id: `onboarding-assistant-${Date.now()}`,
          content: messageContent,
          sender: 'assistant',
          timestamp: new Date().toISOString(),
        };

        const now = new Date().toISOString();
        const defaultContext: ChatContextSelection = {
          clinicalMode: 'all',
          clinicalEntryIds: [],
          journalMode: 'none',
          journalNoteIds: [],
          nutritionMode: 'none',
          nutritionEntryIds: [],
          summaryMode: 'none',
          summaryEntryIds: [],
        };
        const newThread: ChatThread = {
          id: generateUniqueId('chat-thread'),
          title: 'Welcome to EIR',
          createdAt: now,
          updatedAt: now,
          messages: [assistantMessage],
          context: defaultContext,
        };

        const THREAD_STORAGE_KEY = 'eir-chat-threads';
        const existingThreads = persistence.getJSON<ChatThread[]>(THREAD_STORAGE_KEY, []);
        const updatedThreads = [newThread, ...existingThreads];
        persistence.setJSON(THREAD_STORAGE_KEY, updatedThreads);

        const chatPath = getPathForPage('eir-chat');
        navigate(`${chatPath}?threadId=${newThread.id}`);
      } catch (error) {
        console.error('Error creating onboarding chat thread:', error);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkPending = () => {
      if (onboardingProcessedRef.current) return;

      const pending = persistence.getJSON<{
        recommendations: FeatureRecommendation[];
        answers: OnboardingAnswer;
      } | null>('eir-onboarding-complete-pending', null);
      if (!pending) return;

      onboardingProcessedRef.current = true;

      try {
        const { recommendations, answers } = pending;
        persistence.removeItem('eir-onboarding-complete-pending');

        if (!recommendations || !Array.isArray(recommendations) || recommendations.length === 0) {
          console.error('No recommendations found in pending data');
          onboardingProcessedRef.current = false;
          return;
        }

        const topRecommendations = getTopRecommendations(recommendations, 5);
        if (!topRecommendations || topRecommendations.length === 0) {
          console.error('No top recommendations found');
          onboardingProcessedRef.current = false;
          return;
        }

        const forYouItems: ForYouItem[] = topRecommendations.map((rec, index) => {
          const itemType: ForYouItemType =
            rec.page === 'health-assessment'
              ? 'assessment'
              : rec.page === 'health-programs'
                ? 'cbt-program'
                : rec.page === 'personal-growth'
                  ? 'guide'
                  : 'guide';

          const tags = [rec.category, 'recommended'];
          if (rec.subPage) tags.push(`subPage:${rec.subPage}`);

          return {
            id: `onboarding-rec-${rec.id}-${Date.now()}-${index}`,
            title: rec.title,
            description: rec.description,
            type: itemType,
            sourcePage: rec.page,
            tags,
            addedAt: new Date().toISOString(),
          };
        });

        forYouItems.forEach((item) => {
          dispatch({ type: 'ADD_FOR_YOU_ITEM', payload: item });
        });

        createOnboardingChatThread(answers, topRecommendations);
      } catch (error) {
        console.error('Failed to process onboarding completion:', error);
        onboardingProcessedRef.current = false;
      }
    };

    const handleOnboardingComplete = () => {
      onboardingProcessedRef.current = false;
      setTimeout(() => checkPending(), 100);
    };

    window.addEventListener('eir-onboarding-complete', handleOnboardingComplete);
    checkPending();

    const interval = setInterval(checkPending, 200);
    const timeout = setTimeout(() => clearInterval(interval), 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      window.removeEventListener('eir-onboarding-complete', handleOnboardingComplete);
    };
  }, [dispatch, createOnboardingChatThread]);
}
